const TO_RADIANS = Math.PI / 180;

interface Crop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Draws a cropped, rotated, and scaled section of an image onto a canvas.
 */
const getCanvasPreview = async (
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  crop: Crop,
  scale: number = 1,
  rotate: number = 0,
): Promise<void> => {
  if (!image || !canvas || !crop) {
    throw new Error('Missing required parameters: image, canvas, or crop');
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('No 2D context available');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio || 1;

  // Set canvas size for crisp high-DPI rendering
  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  // Move to the start of the crop area
  ctx.translate(-cropX, -cropY);

  // Move to image center for rotation
  ctx.translate(centerX, centerY);

  // Apply rotation
  ctx.rotate(rotateRads);

  // Apply scaling
  ctx.scale(scale, scale);

  // Move back to top-left corner
  ctx.translate(-centerX, -centerY);

  // Draw the image
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
  );

  ctx.restore();
};

/**
 * Returns a canvas element containing the cropped image.
 */
export const getCropPreview = async (
  image: HTMLImageElement,
  crop: Crop,
  scale: number = 1,
  rotate: number = 0,
): Promise<HTMLCanvasElement> => {
  const canvas = document.createElement('canvas');
  await getCanvasPreview(image, canvas, crop, scale, rotate);
  return canvas;
};

/**
 * Returns a Base64-encoded image string (useful for <img src="...">).
 */
export const getCropPreviewDataUrl = async (
  image: HTMLImageElement,
  crop: Crop,
  scale: number = 1,
  rotate: number = 0,
  type: string = 'image/jpeg',
  quality: number = 0.92,
): Promise<string> => {
  const canvas = await getCropPreview(image, crop, scale, rotate);
  return canvas.toDataURL(type, quality);
};

/**
 * Returns a Blob of the cropped image (useful for uploads).
 */
export const getCropPreviewBlob = async (
  image: HTMLImageElement,
  crop: Crop,
  scale: number = 1,
  rotate: number = 0,
  type: string = 'image/jpeg',
  quality: number = 0.92,
): Promise<Blob> => {
  const canvas = await getCropPreview(image, crop, scale, rotate);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob() returned null'));
      },
      type,
      quality,
    );
  });
};
